import React, { useCallback, useEffect, useRef, useState } from 'react';
import { entityService } from '../../services/api';
import styles from './OrganisationLogo.module.css';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = '.png,.jpg,.jpeg,.svg,.webp';

const initialsOf = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '—';

const OrganisationLogo = () => {
  const fileRef = useRef(null);
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setEntity(await entityService.get());
    } catch (err) {
      setError(err.message || 'Failed to load organisation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;

    setError('');
    setNotice('');

    if (file.size > MAX_BYTES) {
      setError('Logo must be 2MB or smaller.');
      return;
    }

    setBusy(true);
    try {
      const updated = await entityService.uploadLogo(file);
      setEntity((prev) => ({ ...prev, ...updated }));
      setNotice('Logo updated. It will appear across your ERP pages.');
    } catch (err) {
      setError(err.message || 'Failed to upload logo');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await entityService.removeLogo();
      setEntity((prev) => ({ ...prev, logo_url: null, logo_key: null }));
      setNotice('Logo removed.');
    } catch (err) {
      setError(err.message || 'Failed to remove logo');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className={styles.muted}>Loading organisation…</p>;
  }

  if (!entity) {
    return (
      <p className={styles.muted}>
        Your account is not associated with an organisation yet, so there is no
        logo to set. Contact an administrator to be linked to one.
      </p>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.preview}>
        {entity.logo_url ? (
          <img src={entity.logo_url} alt={`${entity.entity_name} logo`} />
        ) : (
          <span className={styles.initials}>{initialsOf(entity.entity_name)}</span>
        )}
      </div>

      <div className={styles.body}>
        <p className={styles.name}>{entity.entity_name}</p>
        <p className={styles.hint}>
          PNG, JPG, SVG or WebP, up to 2MB. Shown at the top of your ERP pages
          for everyone in your organisation.
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={busy}
          >
            {busy ? 'Working…' : entity.logo_url ? 'Replace logo' : 'Upload logo'}
          </button>
          {entity.logo_url && (
            <button
              type="button"
              className={styles.secondary}
              onClick={handleRemove}
              disabled={busy}
            >
              Remove
            </button>
          )}
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.notice}>{notice}</p>}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFile}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default OrganisationLogo;
